import { ReactNode, useState } from 'react';
import { ButtonContainer } from '@/components/global';
import { MessageScreen } from '@/components/MessageScreen/MessageScreen';
import { useConfig } from '@/hooks/useConfig/useConfig';
import { useTransaction } from '@/hooks/useTransaction/useTransaction';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import { getCardText } from '@/utils/getCardText';
import { Button, Container } from '@cof/omni-react';
import { CardsPopup } from './CardsPopup';

export function AuthIntro({
  onAccept,
  onUnrecognized,
  details,
}: {
  onAccept: () => void;
  onUnrecognized: () => void;
  details?: ReactNode;
}) {
  const { locale, content } = useConfig();
  const { publicData } = useTransaction();
  const [showCardsPopup, setShowCardsPopup] = useState(false);

  useAirKeyTracePageView('intro');

  const sendTrace = useAirKeyTrace();

  const cards = publicData?.cardLast4Array || [];

  let airkeyDisplayTitle = publicData?.displayTitle?.[locale] || '';
  let airkeyDisplayMessage = publicData?.displayMessage?.[locale] || '';

  if (!airkeyDisplayTitle && !airkeyDisplayMessage) {
    airkeyDisplayTitle = content.intro.title[locale];
    airkeyDisplayMessage = content.intro.description[locale];
  }

  return (
    <>
      <MessageScreen
        iconPath={''}
        iconSize={0}
        title={airkeyDisplayTitle}
        titleSize={25}
        body={
          <>
            {airkeyDisplayMessage}
            <Container paddingTop="medium">{details}</Container>
          </>
        }
        isButtonAtBottom
        button={
          <ButtonContainer>
            <div className={cn('text-neutral-primary')}>
              {getCardText(
                cards,
                locale,
                !!publicData?.cardProductMap,
                Object.entries(publicData?.cardProductMap || {}),
                () => setShowCardsPopup(true),
              )}
            </div>
            <Button
              size="medium"
              className={cn(
                'text-neutral-inverse',
                'bg-interactive-emphasis-high',
              )}
              onClick={() => {
                sendTrace({
                  view: 'intro',
                  action: 'confirm',
                });
                onAccept();
              }}
            >
              {content.intro.primaryButton?.label[locale] || "Yes, it's me"}
            </Button>
            <Button
              size="medium"
              rank="tertiary"
              onClick={() => {
                sendTrace({
                  view: 'intro',
                  action: 'deny',
                });
                onUnrecognized();
              }}
              className={'text-interactive'}
            >
              {content.intro.secondaryButton?.label[locale] ||
                "No, I don't recognize this"}
            </Button>
          </ButtonContainer>
        }
      />
      {showCardsPopup && (
        <CardsPopup
          onClose={() => setShowCardsPopup(false)}
          showCardImage={!!publicData?.cardProductMap}
        />
      )}
    </>
  );
}
